import React, { useState, useMemo } from 'react';
import './CurriculumPlanTable.css';
import { API_BASE_URL } from '../config';
import CurriculumReportView from './CurriculumReportView';
import { ProgressBar, useSimulatedProgress } from './CurriculumPlanGenerator';
import { sanitizePublicData, sanitizePublicText } from '../utils/publicText';
import {
  isGenericElectivePlaceholder,
  normalizeElectiveName,
} from '../utils/electiveCourses';

const NUMERIC_FIELDS = ['teorik', 'uygulama', 'laboratuvar', 'kredi', 'akts'];
const CHANGE_SUMMARY_FIELDS = [
  ['course_name', 'Ders adı'],
  ['course_type', 'Ders tipi'],
  ['teorik', 'Teorik'],
  ['uygulama', 'Uygulama'],
  ['laboratuvar', 'Laboratuvar'],
  ['kredi', 'Kredi'],
  ['akts', 'AKTS'],
];
const DONEM_AKTS_HEDEFI = 30;
const YIL_AKTS_HEDEFI = 60;

function comparableCourseValue(field, value) {
  if (NUMERIC_FIELDS.includes(field) && value !== '' && value !== null && value !== undefined) {
    const numericValue = Number(value);
    if (Number.isFinite(numericValue)) return numericValue;
  }
  return String(value ?? '').trim();
}

function buildCourseChangeSummary(eskiDers, yeniDers) {
  if (!eskiDers) return '';

  const changes = CHANGE_SUMMARY_FIELDS.flatMap(([field, label]) => {
    const oldValue = comparableCourseValue(field, eskiDers[field]);
    const newValue = comparableCourseValue(field, yeniDers[field]);
    return oldValue === newValue ? [] : [`${label} ${oldValue} → ${newValue}`];
  });

  return changes.length > 0 ? `${changes.join(', ')} olarak güncellendi.` : '';
}

function normalizeAltDersOnerisi(alt) {
  const normalized = typeof alt === 'string'
    ? { course_name: alt }
    : (alt || {});

  return {
    ...normalized,
    course_code: normalized.course_code || '',
    course_name: normalized.course_name || '',
    course_type: normalized.course_type || 'Seçmeli',
    // Eski kayıtlarda durum alanı yoktu; bunlar yeni ders önerileridir.
    durum: normalized.durum || 'yeni',
  };
}

function withKeys(plan) {
  const donemler = (plan && plan.donemler) || [];
  const existingAlternativeNames = new Set(
    donemler.flatMap((d) => (d.dersler || []).flatMap((course) => (
      (course.alt_ders_onerileri || [])
        .map(normalizeAltDersOnerisi)
        .filter((alternative) => (
          alternative.durum !== 'yeni'
          && !isGenericElectivePlaceholder(alternative.course_name)
        ))
        .map((alternative) => normalizeElectiveName(alternative.course_name))
    ))),
  );
  const seenAlternativeNames = new Set();
  return donemler.map((d) => ({
    ...d,
    dersler: (d.dersler || []).map((c, i) => ({
      ...c,
      _key: `${d.donem}-${c.course_code || 'new'}-${i}`,
      alt_ders_onerileri: (c.alt_ders_onerileri || []).flatMap((alt, j) => {
        const normalizedAlt = normalizeAltDersOnerisi(alt);
        const nameKey = normalizeElectiveName(normalizedAlt.course_name);
        if (
          !nameKey
          || isGenericElectivePlaceholder(normalizedAlt.course_name)
          || seenAlternativeNames.has(nameKey)
          || (normalizedAlt.durum === 'yeni' && existingAlternativeNames.has(nameKey))
        ) {
          return [];
        }
        seenAlternativeNames.add(nameKey);
        return [{
          ...normalizedAlt,
          _key: `${d.donem}-${c.course_code || 'new'}-${i}-alt-${j}`,
          _secili: false,
        }];
      }),
    })),
  }));
}

function normalizeCode(code) {
  return String(code || '').trim().toUpperCase();
}

function comparisonCourseCode(course) {
  return normalizeCode(course?.source_course_code || course?.course_code);
}

function finalCourseChoiceId(donem, course) {
  const identity = comparisonCourseCode(course) || normalizeCode(course?.course_name);
  return `${donem}::${identity}`;
}

function numericAkts(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function calculateAktsTotal(courses) {
  // Seçmeli alt dersler birbirinin alternatifidir. Dönem toplamında yalnızca
  // Üstteki "Seçmeli Ders I/II/..." havuzu bir kez sayılır; işaretlenen alt dersler eklenmez.
  return (courses || []).reduce(
    (total, course) => total + numericAkts(course.akts),
    0,
  );
}

function formatAkts(value) {
  return Number(value).toLocaleString('tr-TR', { maximumFractionDigits: 2 });
}

function AktsStatus({
  label, total, target, onEditAkts, isEditing = false,
}) {
  const difference = total - target;
  const isBalanced = Math.abs(difference) < 0.001;
  let message = 'AKTS hedefi sağlandı.';
  if (difference < 0) {
    message = `Hedefe ulaşmak için ${formatAkts(Math.abs(difference))} AKTS eklenmeli.`;
  } else if (difference > 0) {
    message = `Hedef ${formatAkts(difference)} AKTS aşılıyor; ders veya AKTS dağılımı düzenlenmeli.`;
  }

  return (
    <div
      className={`akts-status ${isBalanced ? 'akts-status--ok' : 'akts-status--warning'}`}
      role={isBalanced ? 'status' : 'alert'}
    >
      <div className="akts-status__top">
        <span>{label}</span>
        <strong>{formatAkts(total)}/{formatAkts(target)} AKTS</strong>
      </div>
      <div className="akts-status__message">{message}</div>
      {onEditAkts && (!isBalanced || isEditing) && (
        <button
          type="button"
          className="akts-status__edit-button"
          onClick={onEditAkts}
        >
          {isEditing ? 'Düzenlemeyi Bitir' : 'AKTS’leri Düzenle'}
        </button>
      )}
    </div>
  );
}

function buildDonemComparisons(eskiDonemler, yeniDonemler, kaldirilanlar) {
  const kaldirilanAciklamaByDonem = {};
  (kaldirilanlar || []).forEach((entry) => {
    kaldirilanAciklamaByDonem[entry.donem] = new Map(
      (entry.dersler || []).map((c) => [normalizeCode(c.course_code), c.aciklama || '']),
    );
  });

  const donemNumbers = new Set();
  (eskiDonemler || []).forEach((d) => donemNumbers.add(d.donem));
  (yeniDonemler || []).forEach((d) => donemNumbers.add(d.donem));

  const eskiByDonem = Object.fromEntries((eskiDonemler || []).map((d) => [d.donem, d.dersler || []]));
  const yeniByDonem = Object.fromEntries((yeniDonemler || []).map((d) => [d.donem, d.dersler || []]));

  return Array.from(donemNumbers)
    .sort((a, b) => a - b)
    .map((donem) => {
      const kaldirilanAciklama = kaldirilanAciklamaByDonem[donem] || new Map();
      const eskiDonemDersler = eskiByDonem[donem] || [];
      const eskiDersByCode = new Map(
        eskiDonemDersler.map((c) => [normalizeCode(c.course_code), c]),
      );
      const yeniDersler = (yeniByDonem[donem] || []).map((c) => {
        const eskiDers = eskiDersByCode.get(comparisonCourseCode(c));
        const degisiklikOzeti = eskiDers ? buildCourseChangeSummary(eskiDers, c) : '';
        const gorunenDurum = c.durum === 'yeni'
          ? 'yeni'
          : (degisiklikOzeti ? 'güncellendi' : 'aynı');
        return {
          ...c,
          _degisiklikOzeti: degisiklikOzeti,
          _gorunenDurum: gorunenDurum,
        };
      });
      const yeniDurumByCode = new Map(
        yeniDersler.map((c) => [comparisonCourseCode(c), c._gorunenDurum]),
      );
      const yeniDersByCode = new Map(
        yeniDersler.map((c) => [comparisonCourseCode(c), c]),
      );
      const eskiDersler = eskiDonemDersler.map((c) => {
        const codeNorm = normalizeCode(c.course_code);
        const aciklama = kaldirilanAciklama.get(codeNorm);
        const yeniDers = yeniDersByCode.get(codeNorm);
        const altDurumByCode = new Map(
          (yeniDers?.alt_ders_onerileri || [])
            .filter((alt) => alt.durum !== 'yeni')
            .map((alt) => [normalizeCode(alt.course_code), alt.durum]),
        );
        const altDurumByName = new Map(
          (yeniDers?.alt_ders_onerileri || [])
            .filter((alt) => alt.durum !== 'yeni')
            .map((alt) => [normalizeCode(alt.course_name), alt.durum]),
        );
        let rowState = 'aynı';
        if (aciklama !== undefined) {
          rowState = 'kaldirildi';
        } else if (yeniDurumByCode.get(codeNorm) === 'güncellendi') {
          rowState = 'güncellendi';
        }
        return {
          ...c,
          _rowState: rowState,
          _aciklama: aciklama || '',
          alt_dersler: (c.alt_dersler || []).map((alt) => ({
            ...alt,
            _rowState: (
              altDurumByCode.get(normalizeCode(alt.course_code))
              || altDurumByName.get(normalizeCode(alt.course_name))
            ) === 'kaldirildi' ? 'kaldirildi' : 'aynı',
          })),
        };
      });
      return { donem, eskiDersler, yeniDersler };
    });
}

function CurriculumPlanTable({
  programId, programName, durationYear, eskiPlan, initialYeniPlan, kaldirilanlar,
  programOutcomes = [], userType, programAdminToken, adminToken, onRegenerate,
}) {
  const [yeniPlan, setYeniPlan] = useState(() => ({ donemler: withKeys(initialYeniPlan) }));
  const [editingCell, setEditingCell] = useState(null); // { donem, rowKey, field }
  const [editingAktsDonem, setEditingAktsDonem] = useState(null);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [reportError, setReportError] = useState('');
  const [viewMode, setViewMode] = useState('plan'); // 'plan' | 'rapor'
  const [reportData, setReportData] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const [downloadingFinalPlanPdf, setDownloadingFinalPlanPdf] = useState(false);
  const [finalPlanPdfError, setFinalPlanPdfError] = useState('');
  const [finalCourseChoices, setFinalCourseChoices] = useState(() => {
    const choices = {};
    ((initialYeniPlan && initialYeniPlan.donemler) || []).forEach((donemEntry) => {
      (donemEntry.dersler || []).forEach((course) => {
        choices[finalCourseChoiceId(donemEntry.donem, course)] = 'new';
      });
    });
    return choices;
  });
  const reportProgress = useSimulatedProgress();

  const eskiDonemler = (eskiPlan && eskiPlan.donemler) || [];

  const donemComparisons = useMemo(
    () => buildDonemComparisons(eskiDonemler, yeniPlan.donemler, kaldirilanlar || []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [eskiPlan, yeniPlan.donemler, kaldirilanlar],
  );

  const aktsSummary = useMemo(() => {
    const regularSemesterNumbers = donemComparisons
      .map((dc) => Number(dc.donem))
      .filter((donem) => Number.isFinite(donem) && donem > 0);
    const derivedDurationYear = regularSemesterNumbers.length > 0
      ? Math.ceil(Math.max(...regularSemesterNumbers) / 2)
      : 0;
    const yearCount = Math.max(Number(durationYear) || 0, derivedDurationYear);
    const totalsBySemester = new Map(
      donemComparisons.map((dc) => [
        Number(dc.donem),
        {
          yeni: calculateAktsTotal(dc.yeniDersler),
        },
      ]),
    );
    const years = Array.from({ length: yearCount }, (_, index) => {
      const firstSemester = index * 2 + 1;
      const secondSemester = firstSemester + 1;
      const firstTotals = totalsBySemester.get(firstSemester) || { yeni: 0 };
      const secondTotals = totalsBySemester.get(secondSemester) || { yeni: 0 };
      return {
        year: index + 1,
        yeni: firstTotals.yeni + secondTotals.yeni,
      };
    });

    return {
      years,
      target: yearCount * YIL_AKTS_HEDEFI,
      yeni: years.reduce((total, year) => total + year.yeni, 0),
    };
  }, [donemComparisons, durationYear]);

  const finalPlan = useMemo(() => (
    donemComparisons.map((comparison) => {
      const oldCourses = comparison.eskiDersler
        .filter((course) => (
          finalCourseChoices[finalCourseChoiceId(comparison.donem, course)] === 'old'
        ))
        .map((course) => ({
          ...course,
          _finalSource: 'Mevcut plandan seçildi',
          _selectedAlternatives: [],
          _availableAlternatives: (course.alt_dersler || [])
            .filter((alternative) => alternative._rowState !== 'kaldirildi'),
        }));
      const newCourses = comparison.yeniDersler
        .filter((course) => (
          finalCourseChoices[finalCourseChoiceId(comparison.donem, course)] === 'new'
        ))
        .map((course) => ({
          ...course,
          _finalSource: 'Önerilen plandan seçildi',
          _selectedAlternatives: (course.alt_ders_onerileri || [])
            .filter((alternative) => alternative._secili && alternative.durum !== 'kaldirildi'),
          _availableAlternatives: (course.alt_ders_onerileri || [])
            .filter((alternative) => alternative.durum !== 'kaldirildi'),
        }));
      return {
        donem: comparison.donem,
        dersler: [...oldCourses, ...newCourses],
      };
    }).filter((donemEntry) => donemEntry.dersler.length > 0)
  ), [donemComparisons, finalCourseChoices]);

  const electivePoolSummary = useMemo(() => (
    finalPlan.flatMap((donemEntry) => (
      donemEntry.dersler
        .filter((course) => (course._availableAlternatives || []).length > 0)
        .map((course) => ({
          donem: donemEntry.donem,
          course,
          alternatives: course._availableAlternatives,
        }))
    ))
  ), [finalPlan]);

  const finalPlanAkts = useMemo(() => (
    finalPlan.reduce(
      (programTotal, donemEntry) => programTotal + calculateAktsTotal(donemEntry.dersler),
      0,
    )
  ), [finalPlan]);

  const updateCourseField = (donem, rowKey, field, value) => {
    setYeniPlan((prev) => ({
      ...prev,
      donemler: prev.donemler.map((d) => (
        d.donem !== donem ? d : {
          ...d,
          dersler: d.dersler.map((c) => (c._key !== rowKey ? c : { ...c, [field]: value })),
        }
      )),
    }));
  };

  const toggleAltDersSecili = (donem, parentRowKey, altKey) => {
    setYeniPlan((prev) => ({
      ...prev,
      donemler: prev.donemler.map((d) => (
        d.donem !== donem ? d : {
          ...d,
          dersler: d.dersler.map((c) => (
            c._key !== parentRowKey ? c : {
              ...c,
              alt_ders_onerileri: (() => {
                const selectedAlternative = (c.alt_ders_onerileri || [])
                  .find((alternative) => alternative._key === altKey);
                const shouldSelect = !selectedAlternative?._secili;
                return (c.alt_ders_onerileri || []).map((alternative) => ({
                  ...alternative,
                  _secili: alternative._key === altKey ? shouldSelect : false,
                }));
              })(),
            }
          )),
        }
      )),
    }));
  };

  const toggleFinalCourseChoice = (donem, course, source) => {
    const choiceId = finalCourseChoiceId(donem, course);
    setFinalCourseChoices((current) => ({
      ...current,
      [choiceId]: current[choiceId] === source ? null : source,
    }));
  };

  const buildAuthHeaders = () => {
    const headers = { 'Content-Type': 'application/json' };
    if (userType === 'programAdmin' && programAdminToken) {
      headers.Authorization = `Bearer ${programAdminToken}`;
    } else if (adminToken) {
      headers.Authorization = `Bearer ${adminToken}`;
    }
    return headers;
  };

  const buildReportPlanPayload = () => sanitizePublicData({
    program_outcomes: programOutcomes,
    eski_plan: eskiPlan,
    yeni_plan: {
      donemler: yeniPlan.donemler.map((d) => {
        const anaDersler = d.dersler.map(({ _key, alt_ders_onerileri, ...course }) => {
          const rest = { ...course };
          delete rest.source_course_code;
          return {
            ...rest,
            alt_ders_onerileri: (alt_ders_onerileri || []).map(({ _key: altKey, _secili, ...alt }) => ({
              ...alt,
              secili: Boolean(_secili),
            })),
          };
        });
        return { donem: d.donem, dersler: anaDersler };
      }),
    },
    kaldirilanlar: kaldirilanlar || [],
  });

  const handleGenerateReport = async () => {
    setGeneratingReport(true);
    setReportError('');
    reportProgress.start(18000);
    try {
      const res = await fetch(`${API_BASE_URL}/api/program/${programId}/ders-plani/rapor-onizleme`, {
        method: 'POST',
        headers: buildAuthHeaders(),
        body: JSON.stringify(buildReportPlanPayload()),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setReportError((json && json.error) || 'Rapor oluşturulamadı, tekrar deneyin');
        reportProgress.reset();
        return;
      }
      reportProgress.complete();
      setReportData(json);
      setDownloadError('');
      setViewMode('rapor');
    } catch (err) {
      setReportError('Rapor oluşturulamadı, tekrar deneyin');
      reportProgress.reset();
    } finally {
      setGeneratingReport(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!reportData) return;
    setDownloadingPdf(true);
    setDownloadError('');
    try {
      const payload = sanitizePublicData({ ...buildReportPlanPayload(), narrative: reportData.narrative });
      const res = await fetch(`${API_BASE_URL}/api/program/${programId}/ders-plani/rapor`, {
        method: 'POST',
        headers: buildAuthHeaders(),
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        setDownloadError((json && json.error) || 'PDF oluşturulamadı, tekrar deneyin');
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeProgramName = String(reportData.program_name || 'Program')
        .trim()
        .replace(/[<>:"/\\|?*]/g, '')
        .replace(/\s+/g, ' ');
      a.download = `${safeProgramName} Ders Planı Raporu.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setDownloadError('PDF oluşturulamadı, tekrar deneyin');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleDownloadFinalPlanPdf = async () => {
    if (finalPlan.length === 0) return;
    setDownloadingFinalPlanPdf(true);
    setFinalPlanPdfError('');
    try {
      const donemler = finalPlan.map((donemEntry) => ({
        donem: donemEntry.donem,
        dersler: donemEntry.dersler.map((course) => ({
          course_code: course.course_code || '',
          course_name: course.course_name || '',
          course_type: course.course_type || '',
          teorik: course.teorik,
          uygulama: course.uygulama,
          laboratuvar: course.laboratuvar,
          kredi: course.kredi,
          akts: course.akts,
          kaynak: course._finalSource,
          secilen_alt_dersler: (course._selectedAlternatives || []).map((alternative) => ({
            course_code: alternative.course_code || '',
            course_name: alternative.course_name || '',
            course_type: alternative.course_type || 'Seçmeli',
            teorik: alternative.teorik,
            uygulama: alternative.uygulama,
            laboratuvar: alternative.laboratuvar,
            kredi: alternative.kredi,
            akts: alternative.akts,
          })),
          havuzdaki_alt_dersler: (course._availableAlternatives || []).map((alternative) => ({
            course_code: alternative.course_code || '',
            course_name: alternative.course_name || '',
            course_type: alternative.course_type || 'Seçmeli',
            teorik: alternative.teorik,
            uygulama: alternative.uygulama,
            laboratuvar: alternative.laboratuvar,
            kredi: alternative.kredi,
            akts: alternative.akts,
            secili: Boolean(alternative._secili),
          })),
        })),
      }));
      const response = await fetch(`${API_BASE_URL}/api/program/${programId}/ders-plani/nihai-pdf`, {
        method: 'POST',
        headers: buildAuthHeaders(),
        body: JSON.stringify({ donemler, program_outcomes: programOutcomes }),
      });
      if (!response.ok) {
        const json = await response.json().catch(() => ({}));
        setFinalPlanPdfError(json.error || 'Nihai ders planı PDF’i oluşturulamadı.');
        return;
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      const safeProgramName = String(programName || 'Program')
        .trim()
        .replace(/[<>:"/\\|?*]/g, '')
        .replace(/\s+/g, ' ')
        .replace(/[. ]+$/g, '') || 'Program';
      anchor.download = `${safeProgramName} - Ders Planı.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setFinalPlanPdfError('Nihai ders planı PDF’i oluşturulamadı.');
    } finally {
      setDownloadingFinalPlanPdf(false);
    }
  };

  const renderEditableCell = (donem, course, field) => {
    if (field === 'course_code') {
      return (
        <td className="course-code-status-cell">
          <span>{course[field]}</span>
          {course._gorunenDurum === 'yeni' && (
            <span className="diff-badge diff-badge--added">Yeni eklendi</span>
          )}
        </td>
      );
    }
    if (field !== 'akts') {
      return <td>{course[field]}</td>;
    }

    const isElective = (
      String(course.course_type || '').trim().toLocaleLowerCase('tr-TR') === 'seçmeli'
      || (course.alt_ders_onerileri || []).length > 0
    );
    if (isElective) {
      return <td title="Seçmeli ders AKTS değeri sabit 3'tür">3</td>;
    }
    if (course.zorunlu_degerleri_korumali) {
      return (
        <td title="Mevcut onaylı zorunlu dersin kredi ve AKTS değeri değiştirilemez">
          {course.akts}
        </td>
      );
    }

    const isBulkAktsEditing = field === 'akts' && editingAktsDonem === donem;
    const isIndividualEditing = editingCell
      && editingCell.donem === donem
      && editingCell.rowKey === course._key
      && editingCell.field === field;
    const isEditing = isBulkAktsEditing || isIndividualEditing;

    if (!isEditing) {
      return (
        <td
          onClick={() => setEditingCell({ donem, rowKey: course._key, field })}
          className="editable-cell"
          title="AKTS değerini düzenlemek için tıklayın"
        >
          <span>{course[field]}</span>
          <span className="editable-cell__icon" aria-hidden="true">✎</span>
        </td>
      );
    }

    return (
      <td>
        <input
          autoFocus={Boolean(isIndividualEditing && !isBulkAktsEditing)}
          type={NUMERIC_FIELDS.includes(field) ? 'number' : 'text'}
          min={field === 'akts' ? 0 : undefined}
          step={field === 'akts' ? 0.5 : undefined}
          value={course[field]}
          onChange={(e) => updateCourseField(donem, course._key, field, e.target.value)}
          onBlur={() => {
            if (!isBulkAktsEditing) setEditingCell(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !isBulkAktsEditing) setEditingCell(null);
          }}
          aria-label={`${course.course_name || course.course_code} AKTS`}
          className={field === 'akts' ? 'akts-inline-input' : undefined}
          style={{ width: '100%' }}
        />
      </td>
    );
  };

  if (generatingReport && !reportData) {
    return (
      <div className="curriculum-report-loading">
        <ProgressBar label="Program çıktıları ve alana özgü esaslara göre rapor oluşturuluyor..." percent={reportProgress.percent} />
      </div>
    );
  }

  if (viewMode === 'rapor' && reportData) {
    return (
      <CurriculumReportView
        programName={reportData.program_name}
        departmentName={reportData.department_name}
        durationYear={reportData.duration_year}
        educationLevel={reportData.education_level}
        donemComparisons={donemComparisons}
        narrative={reportData.narrative}
        onBack={() => setViewMode('plan')}
        onDownloadPdf={handleDownloadPdf}
        downloadingPdf={downloadingPdf}
        downloadError={downloadError}
      />
    );
  }

  return (
    <div>
      <div className="curriculum-plan-actions">
        <button
          onClick={handleGenerateReport}
          disabled={generatingReport}
          className="button curriculum-plan-actions__report"
        >
          {generatingReport ? 'Ders Planı Raporu Oluşturuluyor...' : 'Ders Planı Raporu Oluştur'}
        </button>
        <button onClick={onRegenerate} disabled={generatingReport} className="button curriculum-plan-actions__regenerate">
          Ders Planını Yeniden Oluştur
        </button>
      </div>

      {reportError && (
        <div className="report-error" role="alert" style={{ marginBottom: 16 }}>
          {reportError}
        </div>
      )}

      <div className="curriculum-diff__legend">
        <span><i className="legend-dot legend-dot--added" /> Yeni eklenen</span>
        <span><i className="legend-dot legend-dot--updated" /> Güncellendi</span>
        <span><i className="legend-dot legend-dot--removed" /> Kaldırılan</span>
        <span><i className="legend-dot legend-dot--same" /> Değişmedi</span>
      </div>

      {donemComparisons.map((dc) => {
        const isRegularSemester = Number(dc.donem) > 0;
        const yeniDonemAkts = calculateAktsTotal(dc.yeniDersler);

        return (
        <div className="donem-block" key={dc.donem}>
          <div className="donem-block__header">{dc.donem}. Dönem</div>
          <div className="donem-block__grid">
            <div className="donem-side donem-side--eski">
              <div className="donem-side__label">Mevcut Plan</div>
              {dc.eskiDersler.length === 0 ? (
                <div className="donem-side--empty">Bu dönem yeni planda eklendi, eski planda karşılığı yok.</div>
              ) : (
                <div className="diff-table-container">
                  <table className="diff-table diff-table--course-choice">
                    <thead>
                      <tr>
                        <th className="course-choice-column">Seç</th>
                        <th>Ders Kodu</th><th>Ders Adı</th><th>Ders Tipi</th><th>Teorik</th>
                        <th>Uygulama</th><th>Laboratuvar</th><th>Kredi</th><th>AKTS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dc.eskiDersler.map((c, i) => (
                        <React.Fragment key={i}>
                          <tr
                            className={
                              c._rowState === 'kaldirildi' ? 'diff-row diff-row--removed'
                              : c._rowState === 'güncellendi' ? 'diff-row diff-row--updated'
                              : 'diff-row diff-row--same'
                            }
                          >
                            <td className="course-choice-cell">
                              <input
                                type="checkbox"
                                checked={finalCourseChoices[finalCourseChoiceId(dc.donem, c)] === 'old'}
                                aria-label={`${c.course_name} dersinin mevcut plandaki sürümünü seç`}
                                onChange={() => toggleFinalCourseChoice(dc.donem, c, 'old')}
                              />
                            </td>
                            <td className="course-code-status-cell">
                              <span>{c.course_code}</span>
                              {c._rowState === 'kaldirildi' && <span className="diff-badge diff-badge--removed">Kaldırıldı</span>}
                              {c._rowState === 'güncellendi' && <span className="diff-badge diff-badge--updated">Güncellendi</span>}
                            </td>
                            <td>{c.course_name}</td>
                            <td>{c.course_type}</td>
                            <td>{c.teorik}</td>
                            <td>{c.uygulama}</td>
                            <td>{c.laboratuvar}</td>
                            <td>{c.kredi}</td>
                            <td>{c.akts}</td>
                          </tr>
                          {c._aciklama && (
                            <tr className="diff-explanation-row">
                              <td colSpan={9} className="diff-explanation">{sanitizePublicText(c._aciklama)}</td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="donem-side donem-side--yeni">
              <div className="donem-side__label">Önerilen Yeni Plan</div>
              {dc.yeniDersler.length === 0 ? (
                <div className="donem-side--empty">Bu dönemde yeni planda ders bulunmuyor.</div>
              ) : (
                <div className="diff-table-container">
                  <table className="diff-table diff-table--course-choice">
                    <thead>
                      <tr>
                        <th className="course-choice-column">Seç</th>
                        <th>Ders Kodu</th><th>Ders Adı</th><th>Ders Tipi</th><th>Teorik</th>
                        <th>Uygulama</th><th>Laboratuvar</th><th>Kredi</th><th>AKTS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dc.yeniDersler.map((c) => (
                        <React.Fragment key={c._key}>
                          <tr
                            className={
                              c._gorunenDurum === 'yeni'
                                ? 'diff-row diff-row--added'
                                : c._gorunenDurum === 'güncellendi'
                                ? 'diff-row diff-row--updated'
                                : 'diff-row diff-row--same'
                            }
                          >
                            <td className="course-choice-cell">
                              <input
                                type="checkbox"
                                checked={finalCourseChoices[finalCourseChoiceId(dc.donem, c)] === 'new'}
                                aria-label={`${c.course_name} dersinin önerilen plandaki sürümünü seç`}
                                onChange={() => toggleFinalCourseChoice(dc.donem, c, 'new')}
                              />
                            </td>
                            {renderEditableCell(dc.donem, c, 'course_code')}
                            {renderEditableCell(dc.donem, c, 'course_name')}
                            {renderEditableCell(dc.donem, c, 'course_type')}
                            {renderEditableCell(dc.donem, c, 'teorik')}
                            {renderEditableCell(dc.donem, c, 'uygulama')}
                            {renderEditableCell(dc.donem, c, 'laboratuvar')}
                            {renderEditableCell(dc.donem, c, 'kredi')}
                            {renderEditableCell(dc.donem, c, 'akts')}
                          </tr>
                          {c._gorunenDurum === 'güncellendi' && c._degisiklikOzeti && (
                            <tr className="diff-explanation-row">
                              <td colSpan={9} className="diff-explanation">{c._degisiklikOzeti}</td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="editable-table-guide">
                <span aria-hidden="true">✎</span>
                Mevcut onaylı zorunlu derslerin kredi ve AKTS değerleri korunur; seçmeli dersler sabit 3 AKTS'dir
              </div>
              {isRegularSemester && (
                <AktsStatus
                  label={`${dc.donem}. Dönem AKTS`}
                  total={yeniDonemAkts}
                  target={DONEM_AKTS_HEDEFI}
                  isEditing={editingAktsDonem === dc.donem}
                  onEditAkts={() => {
                    setEditingCell(null);
                    setEditingAktsDonem((current) => (
                      current === dc.donem ? null : dc.donem
                    ));
                  }}
                />
              )}
            </div>
          </div>
        </div>
        );
      })}

      {aktsSummary.years.length > 0 && (
        <section className="akts-program-summary" aria-labelledby="akts-program-summary-title">
          <h3 id="akts-program-summary-title">Önerilen Plan AKTS Kontrolü</h3>
          <div className="akts-year-list">
            {aktsSummary.years.map((year) => (
              <div className="akts-year-row akts-year-row--single" key={year.year}>
                <AktsStatus
                  label={`${year.year}. Yıl AKTS`}
                  total={year.yeni}
                  target={YIL_AKTS_HEDEFI}
                />
              </div>
            ))}
          </div>
          <div className="akts-program-total akts-program-total--single">
            <AktsStatus
              label="Program Toplamı"
              total={aktsSummary.yeni}
              target={aktsSummary.target}
            />
          </div>
        </section>
      )}

      <section className="final-curriculum-plan" aria-labelledby="final-curriculum-plan-title">
        <div className="final-curriculum-plan__header">
          <div>
            <span className="final-curriculum-plan__tag">Seçiminize göre oluşturuldu</span>
            <h3 id="final-curriculum-plan-title">Nihai Yeni Ders Planı</h3>
            <p>
              Yukarıdaki mevcut ve önerilen plan tablolarındaki “Seç” kutularını kullanarak
              nihai plana girecek sürümü belirleyebilirsiniz. Aynı dersin yalnızca bir sürümü seçilebilir.
            </p>
          </div>
          <div className="final-curriculum-plan__summary">
            <span>{finalPlan.reduce((total, entry) => total + entry.dersler.length, 0)} ders</span>
            <strong>{formatAkts(finalPlanAkts)} AKTS</strong>
          </div>
        </div>

        {finalPlan.length === 0 ? (
          <div className="final-curriculum-plan__empty">
            Nihai planı oluşturmak için mevcut veya önerilen plandan en az bir ders seçin.
          </div>
        ) : (
          <div className="final-curriculum-plan__periods">
            {finalPlan.map((donemEntry) => (
              <article className="final-curriculum-plan__period" key={donemEntry.donem}>
                <div className="final-curriculum-plan__period-header">
                  <strong>{donemEntry.donem}. Dönem</strong>
                  <span>{formatAkts(calculateAktsTotal(donemEntry.dersler))} AKTS</span>
                </div>
                <div className="diff-table-container">
                  <table className="diff-table final-curriculum-plan__table">
                    <thead>
                      <tr>
                        <th>Ders Kodu</th><th>Ders Adı</th><th>Ders Tipi</th><th>Teorik</th>
                        <th>Uygulama</th><th>Laboratuvar</th><th>Kredi</th><th>AKTS</th><th>Kaynak</th>
                      </tr>
                    </thead>
                    <tbody>
                      {donemEntry.dersler.map((course, courseIndex) => (
                        <tr key={`${finalCourseChoiceId(donemEntry.donem, course)}-${courseIndex}`}>
                          <td>{course.course_code}</td>
                          <td>{course.course_name}</td>
                          <td>{course.course_type}</td>
                          <td>{course.teorik}</td>
                          <td>{course.uygulama}</td>
                          <td>{course.laboratuvar}</td>
                          <td>{course.kredi}</td>
                          <td>{course.akts}</td>
                          <td>
                            <span className={`final-source-badge ${course._finalSource.startsWith('Mevcut') ? 'final-source-badge--old' : 'final-source-badge--new'}`}>
                              {course._finalSource.startsWith('Mevcut') ? 'Mevcut' : 'Önerilen'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </article>
            ))}
          </div>
        )}

        {electivePoolSummary.length > 0 && (
          <section className="all-elective-pool" aria-labelledby="all-elective-pool-title">
          <div className="all-elective-pool__header">
            <div>
              <span className="elective-pool-tag">Genel havuz</span>
              <h3 id="all-elective-pool-title">Tüm Seçmeli Ders Havuzu</h3>
              <p>
                Nihai yeni ders planındaki bütün seçmeli dersler burada tek liste halinde gösterilir.
              </p>
            </div>
            <strong>
              {electivePoolSummary.reduce((total, pool) => total + pool.alternatives.length, 0)} ders
            </strong>
          </div>

          <div className="diff-table-container all-elective-pool__table-container">
            <table className="diff-table all-elective-pool__table">
              <thead>
                <tr>
                  <th style={{ width: 54 }}>Seç</th><th>Ders Adı</th><th>Ders Tipi</th><th>Teorik</th>
                  <th>Uygulama</th><th>Laboratuvar</th><th>Kredi</th><th>AKTS</th>
                </tr>
              </thead>
              <tbody>
                {electivePoolSummary.flatMap(({ donem, course, alternatives }) => (
                  alternatives.map((alternative) => {
                    const isNew = alternative.durum === 'yeni';
                    return (
                      <tr
                        key={`${finalCourseChoiceId(donem, course)}-${alternative._key || alternative.course_code || alternative.course_name}`}
                        className={`diff-row${isNew ? ' diff-row--added' : ' diff-row--same'}${alternative._secili ? ' elective-suggestion-row--selected' : ''}`}
                      >
                        <td>
                          {isNew ? (
                            <input
                              type="checkbox"
                              checked={!!alternative._secili}
                              aria-label={`${alternative.course_name} dersini seç`}
                              onChange={() => toggleAltDersSecili(
                                donem,
                                course._key,
                                alternative._key,
                              )}
                            />
                          ) : (
                            <span className="all-elective-pool__existing-mark" title="Mevcut seçmeli ders">✓</span>
                          )}
                        </td>
                        <td>
                          <span className="elective-suggestion-name">{alternative.course_name}</span>
                          <span className={`diff-badge ${isNew ? 'diff-badge--added' : 'diff-badge--same'}`}>
                            {isNew ? (alternative._secili ? 'Seçildi' : 'Yeni öneri') : 'Mevcut'}
                          </span>
                        </td>
                        <td>{alternative.course_type || 'Seçmeli'}</td>
                        <td>{alternative.teorik}</td>
                        <td>{alternative.uygulama}</td>
                        <td>{alternative.laboratuvar}</td>
                        <td>{alternative.kredi}</td>
                        <td>{alternative.akts}</td>
                      </tr>
                    );
                  })
                ))}
              </tbody>
            </table>
          </div>
          </section>
        )}

        <div className="final-curriculum-plan__actions">
          {finalPlanPdfError && <div className="final-curriculum-plan__error">{finalPlanPdfError}</div>}
          <button
            type="button"
            className="button final-curriculum-plan__pdf-button"
            disabled={finalPlan.length === 0 || downloadingFinalPlanPdf}
            onClick={handleDownloadFinalPlanPdf}
          >
            {downloadingFinalPlanPdf ? 'PDF Hazırlanıyor...' : 'Nihai Yeni Ders Planını PDF Olarak İndir'}
          </button>
        </div>
      </section>
    </div>
  );
}

export default CurriculumPlanTable;
